<script setup lang="ts">
import { useForm, Field as AFormField } from "vee-validate";
import { z } from "zod";
import { toTypedSchema } from "@vee-validate/zod";
import type { Picture } from "~/components/common/DisplayPicture.vue";

export type AdminNavItem = {
  url: string;
  icon: string;
  label: string;
};

const props = defineProps<{
  name?: string;
  profileImage?: string;
  items: AdminNavItem[];
  uploadCallback: ( form: FormData )=> Promise<string>;
  removeCallback: ()=> Promise<void>;
}>();

const route = useRoute();

const uploadedImage = ref<{ url: string }>();
const fileUpload = ref( null );
const shouldRemoveImage = ref( false );
const modalOpen = ref( false );

const displayImage = computed( () => {
  let result: Picture | undefined = undefined;

  if ( uploadedImage.value ) {
    result = uploadedImage.value;
  } else if ( props.profileImage ) {
    result = { url: props.profileImage };
  }

  if ( shouldRemoveImage.value ) {
    result = undefined;
  }

  return result;
} );

function removeImage () {
  shouldRemoveImage.value = true;
  uploadedImage.value = undefined;
}

// Vee-Validate form setup
const validationSchema = toTypedSchema(
  z.object( {
    fileUpload: z
      .instanceof( File )
      .refine( file => file.size <= 5 * 1024 * 1024, "File size must be less than 5MB" ),
  } ),
);

const { handleSubmit, resetForm } = useForm( {
  validationSchema,
} );

const isUploading = ref( false );

const upload = handleSubmit( async ( values ) => {
  if ( isUploading.value ) return;

  isUploading.value = true;

  try {
    if ( shouldRemoveImage.value ) {
      await props.removeCallback();
      shouldRemoveImage.value = false;
      uploadedImage.value = undefined;
    }

    if ( values.fileUpload ) {
      const formData = new FormData();
      formData.append( "file", values.fileUpload );

      const url = await props.uploadCallback( formData );

      uploadedImage.value = { url };
    }

    resetForm();
    modalOpen.value = false;
  } catch ( error ) {
    console.error( error );
  } finally {
    isUploading.value = false;
  }
} );
</script>

<template>
  <nav class="admin-nav">
    <button
      class="admin-nav__display-picture-wrapper"
      :aria-label="true ? undefined : 'Edit profile image'"
      @click="modalOpen = false">
      <display-picture
        :picture="displayImage"
        :name="name"
        :loading="!name"
        round />

      <span class="admin-nav__display-picture-overlay hidden">
        <a-icon
          icon="fa:solid:pen-square"
          size="2x" />
      </span>
    </button>

    <div class="admin-nav__items-wrapper">
      <ul class="admin-nav__items">
        <li
          v-for="(item, index) in items"
          :key="index"
          class="admin-nav__item"
          :class="{
            'admin-nav__item--active': item.url === route.path,
          }">
          <nuxt-link
            :to="item.url"
            class="admin-nav__link">
            <span class="admin-nav__icon">
              <a-icon :icon="item.icon || 'fa:light:globe'" />
            </span>
            <span class="admin-nav__label">
              {{ item.label }}
            </span>
          </nuxt-link>
        </li>
      </ul>
    </div>

    <AModal
      id="upload-profile-image"
      v-model="modalOpen">
      <form @submit.prevent="upload">
        <ACard>
          <template #header>
            <AModalTitle>
              Upload a profile image
            </AModalTitle>
          </template>

          <template #default>
            <div class="upload-form">
              <!-- Current Image Display -->
              <div>
                <label>Your current image</label>
                <display-picture
                  :picture="displayImage"
                  :name="name"
                  :loading="!name"
                  size="lg"
                  round />

                <a-button
                  v-if="displayImage"
                  variant="link"
                  class="upload-form__remove-image"
                  @click="removeImage">
                  Remove
                </a-button>
              </div>

              <!-- File Upload Field -->
              <a-form-field
                v-slot="{ handleChange, handleBlur }"
                name="fileUpload">
                <a-form-item>
                  <a-form-label>Upload a new image</a-form-label>
                  <a-form-control>
                    <input
                      type="file"
                      @change="handleChange"
                      @blur="handleBlur">
                  </a-form-control>
                  <a-form-message />
                </a-form-item>
              </a-form-field>
            </div>
          </template>

          <template #footer>
            <a-button
              type="submit">
              Save changes
            </a-button>
          </template>
        </ACard>
      </form>
    </AModal>
  </nav>
</template>

<style lang="scss" scoped>
@import '~/assets/styles/_variables.scss';

.admin-nav {
  display: block;
  width: 75px;
  margin-right: 20px;

  &__display-picture-wrapper {
    position: relative;
    padding: 0;
    border: 0;
    background: none;

    &:hover .admin-nav__display-picture-overlay {
      visibility: visible;
    }
  }

  &__display-picture-overlay {
    visibility: hidden;
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba( 0, 0, 0, 0.25 );
    color: #fff;
    border-radius: 100%;
    cursor: pointer;
  }

  &__items-wrapper {
    margin-top: 30px;
    position: relative;
    z-index: 15;
  }

  &__items {
    position: absolute;
    top: 0;
    left: 7.5px;
    display: flex;
    flex-direction: column;
    padding: 0;
    margin: 0 auto;
    list-style: none;
  }

  &__item {
    margin-bottom: 10px;
  }

  &__link {
    display: inline-flex;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    min-width: 60px;
    height: 60px;
    background: var(--bg);
  }

  &__icon {
    display: inline-flex;
    width: 60px;
    justify-content: center;
    align-items: center;
    font-size: 30px;
  }

  &__label {
    display: none;
    font-family: var(--font-family-headings), serif;
    font-weight: 600;
    padding: 5px 15px 5px 0;
    white-space: nowrap;
  }

  &__link:hover {
    box-shadow: 0 0 5px rgba( 0, 0, 0, 0.3 );
    text-decoration: none;
  }

  &__link:hover &__label {
    display: block;
  }

  &__item--active &__link {
    background: var( --primary );
    color: #fff;
  }

  &__item--border {
    &::before {
      content: "";
      display: block;
      width: 60px;
      border-top: 5px solid var( --primary );
      padding-top: 10px;
    }
  }

  @media ( max-width:  600px ) {
    width: 100%;
    margin-right: 0;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-right: 10px;

    .display-picture {
      height: 60px;
      width: 60px;
      flex-basis: 60px;
    }

    &__items-wrapper {
      flex-grow: 1;
      margin-top: 0;
    }

    &__items {
      position: relative;
      flex-direction: row;
      flex-wrap: wrap;
      flex-grow: 1;
      justify-content: space-between;
      margin-left: 10px;
      gap: 5px;
    }

    &__item {
      margin-bottom: 0;
    }

    &__link {
      background: #f2f2f2;
    }

    &__link:hover &__label {
      display: none;
    }

    &__item--border {
      display: flex;

      &::before {
        width: 1px;
        height: 100%;
        border-top: 0;
        border-left: 5px solid var( --primary );
        padding-top: 0;
        padding-left: 10px;
      }
    }
  }

  @media ( max-width:  500px ) {
    .display-picture {
      height: 40px;
      width: 40px;
      flex-basis: 40px;
    }

    &__link {
      min-width: 40px;
      height: 40px;
    }

    &__icon {
      width: 40px;
      font-size: 20px;
    }
  }
}

.dark-mode {
  @media ( max-width: 600px ) {
    .admin-nav__link {
      background: #222;
    }
  }
}

.dark-mode .admin-nav__item:not( .admin-nav__item--active ) .admin-nav__link:hover {
  background: #fff;
  color: #222;
}

.upload-form {
  &__remove-image {
    padding: 0;
    margin-top: 15px;
  }
}

.hidden {
  display: none;
}
</style>
